import { computeCardActiveTime } from './timer.js';

// What the ongoing "on the clock" notification says. Pure: the app and the headless location
// task both build it from store state, so it reads the same whichever one posted last.
// The ticking number (chronoBase) is what you're doing now — the break while on one, else the
// task, else the clock. Day and break totals are text, refreshed on every change.

const hm = (ms) => {
  const m = Math.max(0, Math.floor(ms / 60000));
  const h = Math.floor(m / 60);
  return h ? `${h}h ${String(m % 60).padStart(2, '0')}m` : `${m}m`;
};
const clockTime = (ms) => { const d = new Date(ms); return `${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`; };

// Work time on the clock today: finished work sessions + the one running now. Travel overlays
// are a parallel metric and never count.
export function dayWorkMs({ clock, sessions = [], now, dayStart }) {
  let ms = 0;
  for (const s of sessions) {
    if (s.overlay || s.type === 'travel' || !s.endedAt || s.id === clock?.sessionId) continue;
    if (s.startedAt < dayStart) continue;
    ms += s.duration || Math.max(0, s.endedAt - s.startedAt);
  }
  if (clock?.startedAt) ms += Math.max(0, now - Math.max(clock.startedAt, dayStart));
  return ms;
}

// Break time today: every logged pause on any task that started today, plus the live one.
export function dayBreakMs({ tasks = [], timer, now, dayStart }) {
  let ms = 0;
  for (const t of tasks) {
    for (const e of t.statusLog || []) {
      if (e.type === 'pause' && e.startedAt >= dayStart) ms += e.duration || Math.max(0, (e.endedAt || e.startedAt) - e.startedAt);
    }
  }
  if (timer?.pauseType && timer.pauseStartedAt) ms += Math.max(0, now - timer.pauseStartedAt);
  return ms;
}

// Status-bar chip text: the task, shortened to fit the chip (≈12 characters on a 96 dp chip).
export function chipText(title, fallback = 'Task') {
  const t = String(title || '').trim();
  if (!t) return fallback;
  if (t.length <= 12) return t;
  const cut = t.slice(0, 11).replace(/\s+\S*$/, '');
  return (cut.length >= 4 ? cut : t.slice(0, 11)) + '…';
}

export function buildLiveNotice({ clock, timer, task, tasks = [], sessions = [], now, dayStart }) {
  if (!clock?.startedAt) return null;
  const day = dayWorkMs({ clock, sessions, now, dayStart });
  const breaks = dayBreakMs({ tasks, timer, now, dayStart });
  const onBreak = !!(timer?.pauseType && timer.pauseStartedAt);
  const taskMs = task ? (task.timeSpent || 0) + computeCardActiveTime(timer, now) : 0;
  const since = `in since ${clockTime(clock.startedAt)}`;
  const travel = clock.traveling ? ' · travelling' : '';

  if (onBreak) {
    return {
      title: `On ${timer.pauseContext || 'break'}${task ? ` · ${task.title}` : ''}`,
      text: `Day ${hm(day)} ${since} · Breaks ${hm(breaks)}${task ? ` · Task ${hm(taskMs)}` : ''}`,
      chronoBase: timer.pauseStartedAt,
      chip: timer.pauseContext ? chipText(timer.pauseContext, 'Break') : 'Break',
    };
  }
  if (task && timer?.startedAt) {
    return {
      title: task.title,
      text: `Day ${hm(day)} ${since} · Breaks ${hm(breaks)}${travel}`,
      chronoBase: now - taskMs,
      chip: chipText(task.title),
    };
  }
  return {
    title: 'On the clock',
    text: `Day ${hm(day)} ${since} · Breaks ${hm(breaks)}${travel}`,
    chronoBase: now - day,
    chip: 'Clock',
  };
}

/**
 * A gentle in-app notice when a counter or metric crosses its goal (CFO 4.4) — shown on the screen, never pushed.
 * crossed = metrics.js goalCrossed(...) ('reached' | 'over' | null). → { title, text, tone } or null.
 */
export function buildGoalNotice(def, crossed, { total = null } = {}) {
  if (!def?.goal || !crossed) return null;
  const per = def.goal.period === 'week' ? 'this week' : def.goal.period === 'month' ? 'this month' : 'today';
  const unit = def.unit ? ` ${def.unit}` : '';
  const at = total != null ? `${Math.round(total * 100) / 100}${unit}` : null;
  if (crossed === 'over') return { title: `${def.name}: over your goal ${per}`, text: `${at ? `${at} — ` : ''}your goal is at most ${def.goal.value}${unit}.`, tone: 'warning' };
  return { title: `${def.name}: goal reached ${per}`, text: `${at ? `${at} — ` : ''}your goal was ${def.goal.value}${unit}.`, tone: 'success' };
}

/**
 * A budget reached an alert level (CFO 5.2): a gentle in-app notice for the owner, never pushed. `fmt(row, v)` writes
 * a figure in the budget's measure (budgets.js budgetFigure). → { title, text, tone } or null.
 */
export function buildBudgetNotice(row, fmt = (r, v) => String(v)) {
  if (!row || row.alert == null) return null;
  const over = row.alert >= 100;
  const left = Number(row.budget) - Number(row.actual);
  return {
    title: `${row.name}: ${over ? (row.pct > 100 ? 'over budget' : 'budget used up') : `${row.alert}% of the budget used`}`,
    text: `${fmt(row, row.actual)} of ${fmt(row, row.budget)} this ${row.period || 'month'}${over ? (left < 0 ? ` — ${fmt(row, -left)} over` : '') : ` — ${fmt(row, left)} left`}${row.forecastEnd != null && !over ? `; on pace for ${fmt(row, row.forecastEnd)}` : ''}.`,
    tone: over ? 'warning' : 'info',
  };
}

/**
 * Several budgets reached an alert level at once (CFO 5.2, v0.1.195): one notice for all of them instead of a stack —
 * "3 budgets need a look", their names underneath, opening the CFO budgets. One budget keeps its own notice
 * (buildBudgetNotice). → { title, text, tone } or null.
 */
export function buildBudgetGroupNotice(rows = []) {
  const list = (rows || []).filter((r) => r && r.alert != null);
  if (list.length < 2) return null;
  const over = list.filter((r) => r.alert >= 100).length;
  const names = list.map((r) => r.name || 'Budget');
  const shown = names.length > 3 ? `${names.slice(0, 3).join(', ')} and ${names.length - 3} more` : names.join(', ');
  return {
    title: `${list.length} budgets need a look`,
    text: `${shown}${over ? ` — ${over === list.length ? 'all' : over} at or over budget` : ' — 80% used'}.`,
    tone: over ? 'warning' : 'info',
  };
}
